/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-prototype-builtins */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { map } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';

import { ICON_ICONS } from '../../icons/Icons';
import {
  am,
  IAddress,
  IProperty,
  PaymentDetails,
  Photo,
} from '../constants/interfaces';

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  constructor(
    private sanitizer: DomSanitizer,
    private _snackBar: MatSnackBar
  ) {}

  getIcons(iconNames: string): SafeHtml {
    return (
      iconNames &&
      this.sanitizer.bypassSecurityTrustHtml(ICON_ICONS[iconNames] ?? '')
    );
  }

  filter(payment: PaymentDetails[] | undefined) {
    if (!payment) return [];
    return payment.filter((value) =>
      ['APPROVED', 'SUCCESS'].includes(value.status)
    );
  }

  getFields<T extends object>(property: IProperty, template: T) {
    const data = this.mapToTemplate<T>(property, template);
    return Object.keys(data)
      .map((key) => ({
        key,
        value: data[key as keyof T],
      }))
      .filter(
        (item) => item.value !== undefined && item.value !== null && item.value
      );
  }

  scrollToId(id: string) {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }
  }

  openSnackBar(message: string, action?: string) {
    return this._snackBar.open(message, action, {
      duration: 5000,
      panelClass: ['snackbar'],
    });
  }

  getAmenities(property: IProperty) {
    const template: am = {
      squareFootage: property.squareFootage,
      parkingSpaces: property.parkingSpaces,
      yearBuilt: property.yearBuilt,
      bedrooms: property.bedrooms,
      bathrooms: property.bathrooms,
      kitchen: property.kitchen,
      livingRoom: property.livingRoom,
      balcony: property.balcony,
      floor: property.floor,
      // hall: property.hall,
      permittedUse: property.permittedUse,
      terrainType: property.terrainType,
      zoningType: property.zoningType,
      soilType: property.soilType,
    };
    return Object.keys(template)
      .map((key) => ({
        key,
        value: template[key as keyof am],
      }))
      .filter(
        (item) => item.value !== undefined && item.value !== null && item.value
      );
  }

  // getFeatures(property: IProperty) {
  //   return Object.entries(property.amenities)
  //     .map(([key, value]) => ({
  //       key,
  //       value: value as boolean,
  //     }))
  //     .filter((item) => item.value);
  // }
  getFeatures(property: IProperty): { key: string; value: any }[] {
    if (!property) return [];

    const features: { key: string; value: any }[] = [];
    const commonFeatures = ['totalFloors'];

    // Add common features
    commonFeatures.forEach((key) => {
      if (property[key as keyof IProperty]) {
        features.push({ key, value: property[key as keyof IProperty] });
      }
    });

    // Add property type specific features
    switch (property.propertyType) {
      case 'RESIDENTIAL': {
        const residentialFeatures = [
          'bedrooms',
          'bathrooms',
          'kitchen',
          'livingRoom',
          'balcony',
          'floor',
        ];

        residentialFeatures.forEach((key) => {
          if (property[key as keyof IProperty]) {
            features.push({ key, value: property[key as keyof IProperty] });
          }
        });

        // Add subproperty type specific features
        if (property.residentialPropertyType === 'APARTMENT') {
          if (property.parkingSpaces) {
            features.push({
              key: 'parkingSpaces',
              value: property.parkingSpaces,
            });
          }
        } else if (property.residentialPropertyType === 'HOUSE') {
          if (property.yearBuilt) {
            features.push({ key: 'yearBuilt', value: property.yearBuilt });
          }
        }
        break;
      }

      case 'COMMERCIAL': {
        const commercialFeatures = [
          'totalUnits',
          'availableUnits',
          'parkingSpaces',
          'permittedUse',
        ];

        commercialFeatures.forEach((key) => {
          if (property[key as keyof IProperty]) {
            features.push({ key, value: property[key as keyof IProperty] });
          }
        });

        if (property.commercialPropertyType === 'OFFICE') {
          if (property.floor) {
            features.push({ key: 'floor', value: property.floor });
          }
        } else if (property.commercialPropertyType === 'RETAIL') {
          if (property.squareFootage) {
            features.push({
              key: 'squareFootage',
              value: property.squareFootage,
            });
          }
        }
        break;
      }

      case 'INDUSTRIAL': {
        const industrialFeatures = [
          'squareFootage',
          'yearBuilt',
          'parkingSpaces',
        ];

        industrialFeatures.forEach((key) => {
          if (property[key as keyof IProperty]) {
            features.push({ key, value: property[key as keyof IProperty] });
          }
        });

        // Add subproperty type specific features
        if (property.industrialPropertyType === 'WAREHOUSE') {
          if (property.craneSupplies) {
            features.push({
              key: 'craneSupplies',
              value: property.craneSupplies,
            });
          }
        } else if (property.industrialPropertyType === 'MANUFACTURING') {
          if (property.powerSupply) {
            features.push({ key: 'powerSupply', value: property.powerSupply });
          }
        }
        break;
      }

      case 'LAND': {
        const landFeatures = [
          'squareFootage',
          'terrainType',
          'zoningType',
          'soilType',
        ];

        landFeatures.forEach((key) => {
          if (property[key as keyof IProperty]) {
            features.push({ key, value: property[key as keyof IProperty] });
          }
        });
        break;
      }
    }

    return features;
  }

  removeUnderscoreAndFirstLetterUpperCase(input: string | undefined): string {
    if (!input) return '';
    return input
      .toLowerCase() // Convert entire string to lowercase first
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/\b\w/g, (c) => c.toUpperCase()); // Capitalize first letter of each word
  }
  mergeFormData(...forms: HTMLFormElement[]): FormData {
    const mergedFormData = new FormData();

    forms.forEach((form) => {
      const formData = new FormData(form);

      formData.forEach((value, key) => {
        // Append each key-value pair to the merged FormData
        mergedFormData.append(key, value);
      });
    });

    return mergedFormData;
  }

  isTokenExpired(date: string): boolean {
    const expirationDate = new Date(date).getTime(); // Convert the date string to a timestamp in milliseconds
    const currentTime = Date.now(); // Get the current time in milliseconds
    return expirationDate < currentTime; // Check if the expiration time is in the past
  }

  daysUntil(endDate: Date | string): number {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (typeof endDate === 'string') {
      endDate = new Date(endDate);
    }
    endDate.setHours(0, 0, 0, 0);
    const timeDiff = endDate.getTime() - today.getTime();
    return Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
  }

  getLocationData(location: string | IAddress | undefined) {
    if (!location) return '';
    if (typeof location === 'string') {
      return location;
    }
    return [location.city, location.state, location.country]
      .filter((part) => !!part && part.trim() !== '' && part.trim().toLowerCase() !== 'undefined' && part.trim().toLowerCase() !== 'null')
      .join(', ');
  }

  capitalizeWords(input?: string): string {
    if (!input) return '';
    return input.replace(/\b\w/g, (char) => char.toUpperCase());
  }

  camelCase(input: string): string {
    return input
      .replace(/(?:^\w|[A-Z]|\b\w)/g, (match, index) =>
        index === 0 ? match.toLowerCase() : match.toUpperCase()
      )
      .replace(/\s+/g, '');
  }
  pascalCaseRegex(input: string): string {
    if (!input) return '';

    return input
      .replace(/(?:^|[\s\-_]+)([a-zA-Z])/g, (_, letter) => letter.toUpperCase())
      .replace(/[\s\-_]+/g, '')
      .replace(/^[a-z]/, (match) => match.toUpperCase());
  }

  titleCase(input: string): string {
    if (!input) return '';
    if (input.toUpperCase() === 'N/A') return input;
    return input
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
  getImage(image: Photo | undefined): string | null {
    return image && image.photo.length > 0 ? image.photo[0] : null;
  }
  getInitial(name: string) {
    let ini = '';
    if (name)
      name
        .split(' ')
        .forEach((value) => (ini += value.substring(0, 1).toUpperCase()));
    return ini.substring(0, 2);
  }

  camelCaseToTitleCase(str: string): string {
    // Replace uppercase letters with a space followed by the letter itself
    return (
      str &&
      str
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (firstChar) => firstChar.toUpperCase())
    ); // Optionally capitalize the first letter
  }

  mapToTemplate<T extends object>(obj: any, template: T): Partial<T> {
    const result: Partial<T> = {};

    // Iterate over the keys in the template object
    for (const key in template) {
      if (template.hasOwnProperty(key) && obj.hasOwnProperty(key)) {
        result[key] = obj[key];
      }
    }

    return result;
  }

  mapToInterface<T>(obj: any): Partial<T> {
    const result: Partial<T> = {};

    for (const key in obj) {
      if (obj.hasOwnProperty(key) && key in result) {
        const value = obj[key];

        // Example logic to parse different types, modify as needed
        if (typeof (result as any)[key] === 'number') {
          result[key as keyof T] = parseFloat(value) as T[keyof T];
        } else if (typeof (result as any)[key] === 'boolean') {
          result[key as keyof T] = (value === 'true' ||
            value === '1') as T[keyof T];
        } else {
          result[key as keyof T] = value as T[keyof T];
        }
      }
    }

    return result;
  }

  convertFormDataToJson(formData: FormData): Record<string, any> {
    const jsonObject: Record<string, any> = {};
    formData.forEach((value, key) => {
      if (key === 'type') {
        jsonObject[key] = value.toString().split(',');
        return;
      }
      jsonObject[key] = value === 'null' ? null : value === 'on' ? true : value;
    });
    return jsonObject;
  }

  generateQuery(data: Record<string, any> | null): string {
    if (!data) return '';

    return Object.entries(data)
      .filter(
        ([_, value]) =>
          value != null && value !== '' && value.toString().trim() !== ''
      )
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
      )
      .join('&');
  }

  setLatAndLng(location: {
    lat: number | undefined;
    lng: number | undefined;
    city?: string;
    region?: string;
    country?: string;
  }) {
    const lng: HTMLInputElement | null = document.querySelector('#longitude');
    const lat: HTMLInputElement | null = document.querySelector('#latitude');
    const city: HTMLInputElement | null = document.querySelector('#city');
    const region: HTMLInputElement | null = document.querySelector('#region');
    const country: HTMLInputElement | null = document.querySelector('#country');
    if (lat) lat.value = location.lat + '';
    if (lng) lng.value = location.lng + '';
    if (city) city.value = location.city + '';
    if (region) region.value = location.region + '';
    if (country) country.value = location.country + '';
    return { lat, lng, city, region, country };
  }

  toQueryString(params: Record<string, string>): string {
    return Object.keys(params)
      .map(
        (key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
      )
      .join('&');
  }

  parseJwt(token: string) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );

    return JSON.parse(jsonPayload);
  }

  getEmail() {
    return this.parseJwt(localStorage.getItem('token') ?? '').email;
  }

  triggerDownload(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = filename;
    link.click();

    // Clean up
    window.URL.revokeObjectURL(url);
  }
}

export function formatStrings<T>(
  stringFields: string[],
  utilsService: UtilsService
) {
  return (source: Observable<T>) =>
    source.pipe(
      map((data) => formatObjectStrings(data, stringFields, utilsService))
    );
}

function formatObjectStrings<T>(
  data: T,
  stringFields: string[],
  utilsService: UtilsService
): T {
  if (!data || typeof data !== 'object') return data;

  if (Array.isArray(data)) {
    return data.map((item) =>
      formatObjectStrings(item, stringFields, utilsService)
    ) as T;
  }

  const formatted = { ...data } as any;

  for (const field of stringFields) {
    const keys = field.split('.');
    let current = formatted;

    for (let i = 0; i < keys.length - 1; i++) {
      if (current[keys[i]]) {
        current = current[keys[i]];
      } else {
        break;
      }
    }

    const lastKey = keys[keys.length - 1];
    if (current && typeof current[lastKey] === 'string') {
      current[lastKey] = utilsService.titleCase(current[lastKey]);
    }
  }

  return formatted;
}
